import { useCallback, useEffect, useState } from "react";

/**
 * Dark-mode controller.
 *
 * Source of truth is the `.dark` class on <html> (Tailwind's `darkMode:'class'`
 * strategy). The no-FOUC script in index.html sets that class before first
 * paint from the same `theme` localStorage key this hook reads/writes, so a
 * dark-mode load never flashes light.
 *
 * Preference model:
 *   - localStorage "theme" = "light" | "dark"  → an explicit operator choice.
 *   - absent                                    → "system": follow the
 *     browser's prefers-color-scheme, live.
 * `cycle` steps through all three, so an explicit choice can always be undone
 * (before, the toggle wrote light/dark forever and the browser setting was
 * never consulted again).
 */
export type ThemePref = "system" | "light" | "dark";

const STORAGE_KEY = "theme";
const QUERY = "(prefers-color-scheme: dark)";

const systemPrefersDark = (): boolean =>
  typeof window !== "undefined" && window.matchMedia(QUERY).matches;

const readPref = (): ThemePref => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
};

const writePref = (p: ThemePref): void => {
  try {
    if (p === "system") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, p);
  } catch {
    /* private mode / storage disabled — the choice still holds for the session */
  }
};

// First click always flips what is on screen (the old one-click behaviour),
// the second picks the other explicit theme, the third returns to the system.
const nextPref = (p: ThemePref, systemDark: boolean): ThemePref => {
  if (p === "system") return systemDark ? "light" : "dark";
  const startedFrom: ThemePref = systemDark ? "light" : "dark";
  return p === startedFrom ? (systemDark ? "dark" : "light") : "system";
};

export function useTheme() {
  const [pref, setPref] = useState<ThemePref>(readPref);
  // The OS theme lives in state (not read at render) so a live change
  // re-renders the header icon, not just the <html> class.
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  useEffect(() => {
    const mq = window.matchMedia(QUERY);
    const onChange = () => setSystemDark(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const isDark = pref === "system" ? systemDark : pref === "dark";

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
  }, [isDark]);

  const cycle = useCallback(() => {
    setPref((prev) => {
      const next = nextPref(prev, systemPrefersDark());
      writePref(next);
      return next;
    });
  }, []);

  return { pref, isDark, next: nextPref(pref, systemDark), cycle };
}
